import type { Layout, LayoutItem } from "react-grid-layout/legacy";
import type { ComponentSpec, MessagePart, ProcessedWidgetData } from "../Processing/types";
import type { GridWidgetType } from "../types";
import { getDefaultLayoutForType } from "../types";
import { getWidgetDef } from "../widgetRegistry";

// ── Widget entry (source-of-truth per widget) ────────────────

export interface WidgetEntry {
  id: string;
  type: GridWidgetType;
  spec?: ComponentSpec;
  meta?: Record<string, unknown>;
}

// ── Widget data status ───────────────────────────────────────

export type WidgetDataStatus = "loading" | "ready" | "error";

// ── AI Insights widget (one streamed chat turn per tile) ─────

/**
 * The tile's stream state. `no_access` and `configure_me` are derived by the
 * tile (gate / empty query) and never stored: neither fires a request, so
 * neither has stream state.
 */
export type AiWidgetStatus = "loading" | "streaming" | "done" | "error" | "throttled";

export interface AiWidgetState {
  status: AiWidgetStatus;
  /** Parts appended so far. Kept on error — the error line renders under them. */
  parts: MessagePart[];
  /** Current step.start label (+ detail), cleared on step.done. */
  step: string | null;
  /** Model narration before the first render; cleared on the first part. */
  preview: string | null;
  error: string | null;
  /** Epoch ms until which regenerate stays disabled after a 429. */
  retryAt: number | null;
}

// ── Dashboard state ──────────────────────────────────────────

/**
 * The reducer's state.
 *
 * The PDF path (`buildPdfSnapshots` and the widget cores) also runs in the headless
 * renderer (LambdaFunctions/sandbox-pdf-renderer, src/render.ts), which builds this
 * state by hand with only `widgets`, `processedData` and `aiWidgets`. A read of
 * another field on the PDF path throws on the server and stops the Lambda pipeline.
 * Add the field to the renderer's state in the same change set.
 */
export interface DashboardState {
  dashboardId: string | null;
  name: string;
  specVersion: number;
  loading: boolean;
  error: string | null;

  widgets: Record<string, WidgetEntry>;
  layout: Layout;

  processedData: Record<string, ProcessedWidgetData>;
  widgetStatuses: Record<string, WidgetDataStatus>;
  widgetErrors: Record<string, string>;
  /** Widgets that wait for the refetch a controller edit triggers; they show a corner spinner. */
  refreshingWidgetIds: ReadonlySet<string>;

  /** One entry per ai_insights widget with a live or finished turn. Empty in the headless renderer. */
  aiWidgets: Record<string, AiWidgetState>;
}

// ── Actions ──────────────────────────────────────────────────

export type DashboardAction =
  | { type: "LOAD_START" }
  | {
      type: "LOAD_SUCCESS";
      dashboardId: string;
      name: string;
      widgets: Record<string, WidgetEntry>;
      layout: Layout;
    }
  | { type: "LOAD_FAILURE"; error: string }
  | { type: "ADD_WIDGET"; widget: WidgetEntry }
  | { type: "DUPLICATE_WIDGET"; original: WidgetEntry; newWidget: WidgetEntry }
  | { type: "REMOVE_WIDGET"; widgetId: string }
  | { type: "UPDATE_WIDGET"; widgetId: string; spec: ComponentSpec }
  | { type: "BULK_UPDATE_WIDGETS"; updates: Array<{ widgetId: string; spec: ComponentSpec }> }
  | { type: "UPDATE_LAYOUT"; layout: Layout }
  | {
      type: "SET_WIDGET_DATA";
      widgetId: string;
      data: ProcessedWidgetData;
      status: WidgetDataStatus;
    }
  | { type: "SET_WIDGET_ERROR"; widgetId: string; error: string }
  | { type: "SET_WIDGETS_REFRESHING"; widgetIds: string[] }
  | { type: "CLEAR_WIDGETS_REFRESHING" }
  | { type: "SET_NAME"; name: string }
  | { type: "AI_WIDGET_START"; widgetId: string }
  | { type: "AI_WIDGET_STEP"; widgetId: string; step: string | null }
  | { type: "AI_WIDGET_PREVIEW"; widgetId: string; preview: string | null }
  | { type: "AI_WIDGET_PART"; widgetId: string; part: MessagePart }
  | { type: "AI_WIDGET_DONE"; widgetId: string }
  | { type: "AI_WIDGET_ERROR"; widgetId: string; error: string; retryAt?: number };

// ── Helpers ──────────────────────────────────────────────────

function maxY(layout: Layout): number {
  return layout.length
    ? Math.max(...layout.map((it: LayoutItem) => it.y + it.h))
    : 0;
}

const EMPTY_AI_WIDGET: AiWidgetState = {
  status: "loading", parts: [], step: null, preview: null, error: null, retryAt: null,
};

/** Merge a patch into one widget's stream state, creating the entry if needed. */
function patchAiWidget(
  state: DashboardState,
  widgetId: string,
  patch: Partial<AiWidgetState>,
): DashboardState {
  return {
    ...state,
    aiWidgets: {
      ...state.aiWidgets,
      [widgetId]: { ...(state.aiWidgets[widgetId] ?? EMPTY_AI_WIDGET), ...patch },
    },
  };
}

// ── Reducer ──────────────────────────────────────────────────

export function dashboardReducer(
  state: DashboardState,
  action: DashboardAction,
): DashboardState {
  switch (action.type) {
    case "LOAD_START":
      return { ...state, loading: true, error: null, aiWidgets: {} };

    case "LOAD_SUCCESS":
      return {
        ...state,
        loading: false,
        dashboardId: action.dashboardId,
        name: action.name,
        widgets: action.widgets,
        layout: action.layout,
        processedData: {},
        widgetStatuses: Object.fromEntries(
          Object.keys(action.widgets).map((id) => [id, "loading" as const]),
        ),
      };

    case "LOAD_FAILURE":
      return { ...state, loading: false, error: action.error };

    case "ADD_WIDGET": {
      const y = maxY(state.layout);
      const layoutItem = getDefaultLayoutForType(
        action.widget.type,
        action.widget.id,
        0,
        y,
      );
      return {
        ...state,
        widgets: { ...state.widgets, [action.widget.id]: action.widget },
        layout: [...state.layout, layoutItem],
        widgetStatuses: {
          ...state.widgetStatuses,
          [action.widget.id]: "loading",
        },
      };
    }

    case "DUPLICATE_WIDGET": {
      const srcLayout = state.layout.find(
        (it: LayoutItem) => it.i === action.original.id,
      );
      const y = maxY(state.layout);
      const layoutItem: LayoutItem = srcLayout
        ? { ...srcLayout, i: action.newWidget.id, x: 0, y }
        : getDefaultLayoutForType(action.newWidget.type, action.newWidget.id, 0, y);
      return {
        ...state,
        widgets: { ...state.widgets, [action.newWidget.id]: action.newWidget },
        layout: [...state.layout, layoutItem],
        widgetStatuses: {
          ...state.widgetStatuses,
          [action.newWidget.id]: "loading",
        },
      };
    }

    case "REMOVE_WIDGET": {
      const { [action.widgetId]: _w, ...nextWidgets } = state.widgets;
      const { [action.widgetId]: _d, ...nextData } = state.processedData;
      const { [action.widgetId]: _s, ...nextStatuses } = state.widgetStatuses;
      const { [action.widgetId]: _ai, ...nextAiWidgets } = state.aiWidgets;
      const nextRefreshing = new Set(state.refreshingWidgetIds);
      nextRefreshing.delete(action.widgetId);
      return {
        ...state,
        refreshingWidgetIds: nextRefreshing,
        widgets: nextWidgets,
        layout: state.layout.filter((it: LayoutItem) => it.i !== action.widgetId),
        processedData: nextData,
        widgetStatuses: nextStatuses,
        aiWidgets: nextAiWidgets,
      };
    }

    case "UPDATE_WIDGET":
      return {
        ...state,
        widgets: {
          ...state.widgets,
          [action.widgetId]: {
            ...state.widgets[action.widgetId],
            spec: action.spec,
          },
        },
        widgetStatuses: {
          ...state.widgetStatuses,
          [action.widgetId]: "loading",
        },
      };

    case "BULK_UPDATE_WIDGETS": {
      const nextWidgets = { ...state.widgets };
      const nextStatuses = { ...state.widgetStatuses };
      for (const { widgetId, spec } of action.updates) {
        nextWidgets[widgetId] = { ...nextWidgets[widgetId], spec };
        nextStatuses[widgetId] = "loading";
      }
      return { ...state, widgets: nextWidgets, widgetStatuses: nextStatuses };
    }

    case "UPDATE_LAYOUT": {
      const enriched = action.layout.map((item: LayoutItem) => {
        const wType = state.widgets[item.i]?.type;
        const def = wType ? getWidgetDef(wType) : undefined;
        const mins = def?.defaultLayout;
        if (!mins) return item;
        return { ...item, minW: mins.minW, minH: mins.minH };
      });
      return { ...state, layout: enriched };
    }

    case "SET_WIDGET_DATA":
      return {
        ...state,
        processedData: {
          ...state.processedData,
          [action.widgetId]: action.data,
        },
        widgetStatuses: {
          ...state.widgetStatuses,
          [action.widgetId]: action.status,
        },
      };

    case "SET_WIDGET_ERROR":
      return {
        ...state,
        widgetStatuses: {
          ...state.widgetStatuses,
          [action.widgetId]: "error",
        },
        widgetErrors: {
          ...state.widgetErrors,
          [action.widgetId]: action.error,
        },
      };

    case "SET_WIDGETS_REFRESHING": {
      if (action.widgetIds.length === 0) return state;
      const next = new Set(state.refreshingWidgetIds);
      for (const id of action.widgetIds) next.add(id);
      return { ...state, refreshingWidgetIds: next };
    }

    case "CLEAR_WIDGETS_REFRESHING":
      if (state.refreshingWidgetIds.size === 0) return state;
      return { ...state, refreshingWidgetIds: new Set() };

    case "SET_NAME":
      return { ...state, name: action.name };

    case "AI_WIDGET_START":
      // A retry keeps nothing: a fresh turn replays every part it wants shown.
      return patchAiWidget(state, action.widgetId, {
        status: "loading", parts: [], step: null, preview: null, error: null, retryAt: null,
      });

    case "AI_WIDGET_STEP":
      return patchAiWidget(state, action.widgetId, { step: action.step });

    case "AI_WIDGET_PREVIEW":
      return patchAiWidget(state, action.widgetId, { preview: action.preview });

    case "AI_WIDGET_PART": {
      const current = state.aiWidgets[action.widgetId]?.parts ?? [];
      return patchAiWidget(state, action.widgetId, {
        status: "streaming",
        parts: [...current, action.part],
        preview: null,
      });
    }

    case "AI_WIDGET_DONE":
      return patchAiWidget(state, action.widgetId, { status: "done", step: null, preview: null });

    case "AI_WIDGET_ERROR":
      return patchAiWidget(state, action.widgetId, {
        status: action.retryAt != null ? "throttled" : "error",
        step: null,
        preview: null,
        error: action.error,
        retryAt: action.retryAt ?? null,
      });

    default:
      return state;
  }
}

// ── Factory ──────────────────────────────────────────────────

export function createInitialState(): DashboardState {
  return {
    dashboardId: null,
    name: "",
    specVersion: 1,
    loading: false,
    error: null,
    widgets: {},
    layout: [],
    processedData: {},
    widgetStatuses: {},
    widgetErrors: {},
    aiWidgets: {},
    refreshingWidgetIds: new Set(),
  };
}

// ── Serialization ────────────────────────────────────────────

export interface ServerDashboardPayload {
  id: string;
  name: string;
  spec_version: number;
  widgets: Array<{
    id: string;
    type: string;
    spec?: ComponentSpec;
    meta?: Record<string, unknown>;
  }>;
  layout: Layout;
}

export function toServerPayload(state: DashboardState): ServerDashboardPayload {
  return {
    id: state.dashboardId ?? "",
    name: state.name,
    spec_version: state.specVersion,
    widgets: Object.values(state.widgets).map((w) => ({
      id: w.id,
      type: w.type,
      spec: w.spec,
      meta: w.meta,
    })),
    layout: state.layout,
  };
}

export function fromServerPayload(
  payload: ServerDashboardPayload,
): Pick<DashboardState, "dashboardId" | "name" | "specVersion" | "widgets" | "layout"> {
  const widgets: Record<string, WidgetEntry> = {};
  for (const w of payload.widgets) {
    widgets[w.id] = {
      id: w.id,
      type: w.type as GridWidgetType,
      spec: w.spec,
      meta: w.meta,
    };
  }
  const layout = payload.layout.map((item: LayoutItem) => {
    const widgetType = widgets[item.i]?.type;
    const def = widgetType ? getWidgetDef(widgetType) : undefined;
    const mins = def?.defaultLayout;
    if (!mins) return item;
    return { ...item, minW: mins.minW, minH: mins.minH };
  });

  return {
    dashboardId: payload.id,
    name: payload.name,
    specVersion: payload.spec_version,
    widgets,
    layout,
  };
}
